import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { hasAdminAuthority } from '@nbh/shared';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { AppShell } from './components/AppShell';
import { AdminPage } from './pages/AdminPage';
import { ForcedPasswordChangePage } from './pages/ForcedPasswordChangePage';
import { LoginPage } from './pages/LoginPage';
import { SecuritySettingsPage } from './pages/SecuritySettingsPage';
import { QueuePage } from './pages/QueuePage';
import { ReportPage } from './pages/ReportPage';
import { MessagesPage } from './pages/MessagesPage';
import { NotificationSettingsPage } from './pages/NotificationSettingsPage';
import { TicketPage } from './pages/TicketPage';

/**
 * Routing (SPEC.md §6 Phase 3).
 *
 * Browser history routing rather than hashes, because QR stickers link to real paths
 * like /report?qr=room-302. Nginx already falls back to index.html for unmatched
 * paths, so a deep link opens correctly from a cold start.
 */
export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

function AppRoutes() {
  const { t } = useTranslation();
  const { user, loading } = useAuth();

  // Nothing renders until the session probe settles, or a signed-in user would see
  // the login screen flash on every refresh.
  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-slate-950 text-sm text-slate-400">
        {t('app.loading')}
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* Any deep link while signed out lands on login. */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  /**
   * The third state (decision D8).
   *
   * Authenticated, holding a valid session, and permitted exactly one route — because
   * somebody else set this password and its owner has not replaced it yet. Between the
   * signed-out branch above and the signed-in one below, mirroring the signed-out shape
   * inverted: one route that is allowed, and a catch-all pointing into it.
   *
   * Rendered outside `AppShell` so no unusable navigation shows.
   *
   * This is a convenience, not the control. The same flag is enforced in `JwtAuthGuard`,
   * which refuses every route but three — a caller ignoring this redirect still reaches
   * nothing.
   */
  if (user.mustChangePassword) {
    return (
      <Routes>
        <Route path="/change-password" element={<ForcedPasswordChangePage />} />
        <Route path="*" element={<Navigate to="/change-password" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Signed in, so the login route is no longer somewhere to be. */}
      <Route path="/login" element={<Navigate to="/" replace />} />
      {/* Nothing to change once the flag is clear; the settings route is where a password
          change lives when it is voluntary. */}
      <Route path="/change-password" element={<Navigate to="/settings/security" replace />} />

      <Route element={<AppShell />}>
        <Route index element={<QueuePage />} />
        <Route path="report" element={<ReportPage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="tickets/:id" element={<TicketPage />} />
        {/* The client's first settings route (D41). Not a tab: the bottom bar is the
            four places a technician goes on purpose, and this is somewhere you go once. */}
        <Route path="settings/notifications" element={<NotificationSettingsPage />} />
        {/* Every role, and no authority check: it acts only on the caller's own account. */}
        <Route path="settings/security" element={<SecuritySettingsPage />} />
        <Route
          path="admin"
          element={
            // Authority, not equality: SUPER_ADMIN subsumes ADMIN (D23), and an exact
            // match would redirect the root account away from a page the API serves it.
            hasAdminAuthority(user.role) ? <AdminPage /> : <Navigate to="/" replace />
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
